import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import "@/App.css";

import { AuthProvider } from "@/context/AuthContext";
import { PlatformProvider } from "@/context/PlatformContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import Layout from "@/components/Layout";
import HelpShell from "@/components/layout/HelpShell";

import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import AcceptInvitation from "@/pages/AcceptInvitation";
import Dashboard from "@/pages/Dashboard";
import Leads from "@/pages/Leads";
import LeadDetail from "@/pages/LeadDetail";
import NewLead from "@/pages/NewLead";
import BadLeadsQueue from "@/pages/BadLeadsQueue";
import BadLeadReport from "@/pages/BadLeadReport";
import BadLeadDetail from "@/pages/BadLeadDetail";
import Employees from "@/pages/Employees";
import InviteEmployee from "@/pages/InviteEmployee";
import Organizations from "@/pages/Organizations";
import ManageBuyer from "@/pages/ManageBuyer";
import BuyerSetup from "@/pages/BuyerSetup";
import Campaigns from "@/pages/Campaigns";
import AuditLog from "@/pages/AuditLog";
import CsvUpload from "@/pages/CsvUpload";
import Integrations from "@/pages/Integrations";
import HostedForm from "@/pages/HostedForm";
import Profile from "@/pages/Profile";
import MyActivity from "@/pages/MyActivity";
import QualityAlerts from "@/pages/QualityAlerts";
import FormIntegrations from "@/pages/FormIntegrations";
import ScheduledReports from "@/pages/ScheduledReports";
import DeliveryDestinations from "@/pages/DeliveryDestinations";
import DeliveryHealth from "@/pages/DeliveryHealth";
import ReplacementVouchers from "@/pages/ReplacementVouchers";
import HelpCenter from "@/pages/help/HelpCenter";
import Invoices from "@/pages/Invoices";
import EmailIntake from "@/pages/EmailIntake";
import MfaPolicies from "@/pages/MfaPolicies";
import PlatformSettings from "@/pages/PlatformSettings";
import Status from "@/pages/Status";
import Downloads from "@/pages/Downloads";
import CodeHealth from "@/pages/CodeHealth";
import AffiliateQuality from "@/pages/AffiliateQuality";
import BuyerRejections from "@/pages/BuyerRejections";
import PausedTraffic from "@/pages/PausedTraffic";
import AdminLandingPages from "@/pages/AdminLandingPages";
import AdminMarketplace from "@/pages/AdminMarketplace";
import AdminMargin from "@/pages/AdminMargin";
import AdminDemandMap from "@/pages/AdminDemandMap";
import CustomLandingPage from "@/pages/CustomLandingPage";
import AdminCustomLandingPageEditor from "@/pages/AdminCustomLandingPageEditor";
import AdminPixels from "@/pages/AdminPixels";
import AdminGoogleAnalytics from "@/pages/AdminGoogleAnalytics";
import AdminProspectEngagement from "@/pages/AdminProspectEngagement";
import AdminLpLiveAnalytics from "@/pages/AdminLpLiveAnalytics";
import FailedDeliveries from "@/pages/FailedDeliveries";
import MediaBuyerAttribution from "@/pages/MediaBuyerAttribution";
import MediaBuyerLeads from "@/pages/MediaBuyerLeads";
import TrafficResults from "@/pages/TrafficResults";
import MediaBuyerCustomTracking from "@/pages/MediaBuyerCustomTracking";
import AdminTrackingApprovals from "@/pages/AdminTrackingApprovals";
import Postbacks from "@/pages/Postbacks";
import AdminPostbacks from "@/pages/AdminPostbacks";
import BuyerDashboard from "@/pages/BuyerDashboard";
import VendorDashboard from "@/pages/VendorDashboard";
import LegacyFunnelRedirect from "@/pages/landing/LegacyFunnelRedirect";
import LandingSplit from "@/pages/landing/LandingSplit";
import LandingThankYou from "@/pages/landing/ThankYou";
import LegalPage from "@/pages/landing/LegalPage";
import RotatorRedirect from "@/pages/RotatorRedirect";
import UnsubscribeWeekly from "@/pages/UnsubscribeWeekly";
import KeepPaused from "@/pages/KeepPaused";
import ResendWelcome from "@/pages/ResendWelcome";
import TyReviewPage from "@/pages/TyReviewPage";
import AdminTestLeads from "@/pages/AdminTestLeads";
import AdminCallTaps from "@/pages/AdminCallTaps";
import AdminMenuSettings from "@/pages/AdminMenuSettings";
import RequestInbox from "@/pages/RequestInbox";
import TrafficAccess from "@/pages/TrafficAccess";
import AdminOnboarding from "@/pages/AdminOnboarding";
import AdminOnboardingForm from "@/pages/AdminOnboardingForm";
import AdminLeadRescue from "@/pages/AdminLeadRescue";
import BuyerOnboarding from "@/pages/BuyerOnboarding";
import { SitePixels } from "@/components/SitePixels";
import { LanderHostGuard } from "@/components/LanderHostGuard";
import { AppErrorBoundary } from "@/components/AppErrorBoundary";

// Hoisted to module scope so we don't build a new object on every App render.
const TOAST_OPTIONS = {
  className: "brut-shadow-sm border border-black rounded-none-force",
  style: { fontFamily: "IBM Plex Sans", borderRadius: 0 },
};

function Shell({ children }) {
  return (
    <ProtectedRoute>
      <Layout>{children}</Layout>
    </ProtectedRoute>
  );
}

function App() {
  return (
    <PlatformProvider>
      <AuthProvider>
      <BrowserRouter>
        <Toaster position="top-right" richColors toastOptions={TOAST_OPTIONS} />
        <SitePixels />
        <AppErrorBoundary>
        <LanderHostGuard>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/accept-invitation" element={<AcceptInvitation />} />
          <Route path="/form/:publicId" element={<HostedForm />} />
          <Route path="/onboard/:token" element={<BuyerOnboarding />} />
          <Route path="/status" element={<Status />} />

          {/* Iter-31: Public MVA landing pages — no auth, no dashboard chrome.
              Vendors drive traffic here via ?aff=<public_source_id>&sub=<sub_id>. */}
          <Route path="/lp/auto-accident" element={<LegacyFunnelRedirect to="auto" />} />
          {/* Old vertical URLs forward to the shared case funnels. */}
          <Route path="/lp/truck-accident" element={<LegacyFunnelRedirect to="truck" />} />
          <Route path="/lp/rideshare-accident" element={<LegacyFunnelRedirect to="rideshare" />} />
          <Route path="/lp/motorcycle-accident" element={<LegacyFunnelRedirect to="motorcycle" />} />
          <Route path="/lp/personal-injury" element={<LegacyFunnelRedirect to="injury" />} />
          <Route path="/lp/free-case-review" element={<LegacyFunnelRedirect to="standard" />} />
          {/* Iter-33: A/B split router — vendors send ads here; we bucket 50/50 sticky. */}
          <Route path="/lp/pi" element={<LandingSplit />} />
          <Route path="/lp/thank-you" element={<LandingThankYou />} />
          <Route path="/go/:code" element={<RotatorRedirect />} />
          {/* Iter-43: Dynamic route matches any admin-authored custom slug.
              Must come AFTER the hardcoded verticals above — React Router
              gives static segments precedence but declaration order is a
              belt-and-suspenders safeguard. */}
          <Route path="/lp/:slug" element={<CustomLandingPage />} />
          <Route path="/privacy" element={<LegalPage kind="privacy" />} />
          <Route path="/unsubscribe/weekly" element={<UnsubscribeWeekly />} />
          <Route path="/unsubscribe/alerts" element={<UnsubscribeWeekly kind="alerts" />} />
          <Route path="/keep-paused" element={<KeepPaused />} />
          <Route path="/resend-welcome" element={<ResendWelcome />} />
          <Route path="/terms" element={<LegalPage kind="terms" />} />
          <Route path="/legal-partners" element={<LegalPage kind="partners" />} />
          <Route path="/review/thank-you/:token" element={<TyReviewPage />} />

          {/* Iter-30: Buyer portal — read-only, scoped to buyer_destination_ids. */}
          <Route path="/buyer" element={<ProtectedRoute><BuyerDashboard /></ProtectedRoute>} />
          {/* Iter-30.6: Vendor portal — read-only, scoped to vendor_affiliate_id. */}
          <Route path="/vendor" element={<ProtectedRoute><VendorDashboard /></ProtectedRoute>} />

          <Route path="/" element={<Shell><Dashboard /></Shell>} />
          <Route path="/leads" element={<Shell><Leads /></Shell>} />
          <Route path="/leads/new" element={<Shell><NewLead /></Shell>} />
          <Route path="/leads/:id" element={<Shell><LeadDetail /></Shell>} />
          <Route path="/leads/:id/report" element={<Shell><BadLeadReport /></Shell>} />
          <Route path="/bad-leads" element={<Shell><BadLeadsQueue /></Shell>} />
          <Route path="/bad-leads/:id" element={<Shell><BadLeadDetail /></Shell>} />
          <Route path="/employees" element={<Shell><Employees /></Shell>} />
          <Route path="/employees/invite" element={<Shell><InviteEmployee /></Shell>} />
          <Route path="/clients" element={<Shell><Organizations type="client" /></Shell>} />
          {/* Iter-61: Buyer intake-criteria full-page editor */}
          <Route path="/clients/:id/manage" element={<Shell><ManageBuyer /></Shell>} />
          <Route path="/clients/setup" element={<Shell><BuyerSetup /></Shell>} />
          <Route path="/clients/:id/setup" element={<Shell><BuyerSetup /></Shell>} />
          <Route path="/affiliates" element={<Shell><Organizations type="affiliate" /></Shell>} />          <Route path="/campaigns" element={<Shell><Campaigns /></Shell>} />
          <Route path="/csv-upload" element={<Shell><CsvUpload /></Shell>} />
          <Route path="/integrations" element={<Shell><Integrations /></Shell>} />
          <Route path="/form-integrations" element={<Shell><FormIntegrations /></Shell>} />
          <Route path="/quality-alerts" element={<Shell><QualityAlerts /></Shell>} />
          <Route path="/scheduled-reports" element={<Shell><ScheduledReports /></Shell>} />
          <Route path="/delivery" element={<Shell><DeliveryDestinations /></Shell>} />
          <Route path="/delivery-health" element={<Shell><DeliveryHealth /></Shell>} />
          <Route path="/replacement-vouchers" element={<Shell><ReplacementVouchers /></Shell>} />
          <Route path="/downloads" element={<Shell><Downloads /></Shell>} />
          <Route path="/help" element={<HelpShell><HelpCenter /></HelpShell>} />
          <Route path="/invoices" element={<Shell><Invoices /></Shell>} />
          <Route path="/email-intake" element={<Shell><EmailIntake /></Shell>} />
          <Route path="/mfa-policies" element={<Shell><MfaPolicies /></Shell>} />
          <Route path="/settings/platform" element={<Shell><PlatformSettings /></Shell>} />
          <Route path="/admin/code-health" element={<Shell><CodeHealth /></Shell>} />
          <Route path="/admin/affiliate-quality" element={<Shell><AffiliateQuality /></Shell>} />
          <Route path="/admin/buyer-rejections" element={<Shell><BuyerRejections /></Shell>} />
          <Route path="/admin/paused-traffic" element={<Shell><PausedTraffic /></Shell>} />
          <Route path="/audit" element={<Shell><AuditLog /></Shell>} />
          {/* Iter-34: Admin overview of every landing page. */}
          <Route path="/admin/landing-pages" element={<Shell><AdminLandingPages /></Shell>} />
          {/* Iter-67: GA-style live LP impression + funnel analytics. */}
          <Route path="/admin/lp-analytics" element={<Shell><AdminLpLiveAnalytics /></Shell>} />
          {/* Iter-35: Marketplace admin — pricing + listings. */}
          <Route path="/admin/marketplace" element={<Shell><AdminMarketplace /></Shell>} />
          {/* Iter-41: Margin dashboard — internal-only platform take view. */}
          <Route path="/admin/margin" element={<Shell><AdminMargin /></Shell>} />
          <Route path="/admin/demand-map" element={<Shell><AdminDemandMap /></Shell>} />
          <Route path="/admin/test-leads" element={<Shell><AdminTestLeads /></Shell>} />
          <Route path="/admin/call-taps" element={<Shell><AdminCallTaps /></Shell>} />
          <Route path="/admin/menu" element={<Shell><AdminMenuSettings /></Shell>} />
          <Route path="/admin/requests" element={<Shell><RequestInbox /></Shell>} />
          <Route path="/admin/traffic-access" element={<Shell><TrafficAccess /></Shell>} />
          <Route path="/admin/onboarding" element={<Shell><AdminOnboarding /></Shell>} />
          <Route path="/admin/onboarding/:id" element={<Shell><AdminOnboardingForm /></Shell>} />
          <Route path="/admin/lead-rescue" element={<Shell><AdminLeadRescue /></Shell>} />
          {/* Iter-43: Custom buyer landing pages (admin CRUD) */}
          <Route path="/admin/landing-pages/custom/new" element={<Shell><AdminCustomLandingPageEditor /></Shell>} />
          <Route path="/admin/landing-pages/custom/:id" element={<Shell><AdminCustomLandingPageEditor /></Shell>} />
          {/* Iter-45: Global marketing pixels + media buyer attribution */}
          <Route path="/admin/pixels" element={<Shell><AdminPixels /></Shell>} />
          <Route path="/admin/google-analytics" element={<Shell><AdminGoogleAnalytics /></Shell>} />
          {/* Iter-63.1: Prospect engagement + failed deliveries admin surfaces */}
          <Route path="/admin/prospect-engagement" element={<Shell><AdminProspectEngagement /></Shell>} />
          <Route path="/admin/failed-deliveries" element={<Shell><FailedDeliveries /></Shell>} />
          <Route path="/media-buyer/attribution" element={<Shell><MediaBuyerAttribution /></Shell>} />
          <Route path="/media-buyer/leads" element={<Shell><MediaBuyerLeads /></Shell>} />
          <Route path="/results" element={<Shell><TrafficResults /></Shell>} />
          {/* Iter-56: Media Buyer custom tracking + admin approval queue */}
          <Route path="/media-buyer/custom-tracking" element={<Shell><MediaBuyerCustomTracking /></Shell>} />
          <Route path="/admin/tracking-approvals" element={<Shell><AdminTrackingApprovals /></Shell>} />
          <Route path="/postbacks" element={<HelpShell><Postbacks /></HelpShell>} />
          <Route path="/admin/postbacks" element={<Shell><AdminPostbacks /></Shell>} />
          <Route path="/timeline" element={<Shell><MyActivity /></Shell>} />
          <Route path="/profile" element={<Shell><Profile /></Shell>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </LanderHostGuard>
        </AppErrorBoundary>
      </BrowserRouter>
    </AuthProvider>
    </PlatformProvider>
  );
}

export default App;
